import { Box, Button, Chip, Typography } from "@mui/material";
import { useState } from "react";
import { fetchClientPurchases } from "@/api/purchases";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import BlotterNote from "@/components/BlotterNote";
import PurchaseDetail from "@/components/finance/PurchaseDetail";
import { PURCHASE_STATUS_CHIP, PURCHASE_STATUS_OPTIONS } from "@/constants/purchases";
import { useClientPurchases } from "@/hooks/usePurchases";
import type { ClientPurchase, PurchaseSettlementStatus } from "@/interfaces/purchases";
import { formatDate } from "@/utils/date-format";
import { formatAmount } from "@/utils/number-format";

const money = (v: string | null, ccy = "USD") =>
  v == null ? "—" : `${ccy} ${formatAmount(Number(v))}`;

const statusFilter: FilterField = {
  id: "settlementStatus",
  label: "Status",
  options: PURCHASE_STATUS_OPTIONS,
};

const statusFrom = (filters: AppliedFilter[]): PurchaseSettlementStatus | undefined =>
  (filters.find((f) => f.fieldId === "settlementStatus")?.values[0] as
    | PurchaseSettlementStatus
    | undefined) ?? undefined;

/**
 * Finance › Client Purchases — every credit purchase across clients, for reconciling Stripe payouts
 * against the bank. GFG-only (view/FINANCE, scope all); clients see their own money-in through
 * Group › Transactions.
 *
 * Filter on Clearing to find purchases whose card cleared but whose payout hasn't been seen. Those
 * are the ones that may need recording by hand if a webhook was missed.
 */
export default function ClientPurchasesPanel() {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  if (selectedId != null) {
    return <PurchaseDetail purchaseId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<ClientPurchase> => {
    const { data, isLoading } = useClientPurchases({
      search: q.search || undefined,
      settlementStatus: statusFrom(q.filters),
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<ClientPurchase> = {
    title: "Client Purchases",
    searchPlaceholder: "Search purchase no, organization, group, PaymentIntent or payout id",
    itemNoun: "purchase",
    rowKey: (p) => p.id,
    defaultSortKey: "created",
    defaultSortDir: "desc",
    filterFields: [statusFilter],
    useData,
    exportFilename: "client-purchases.csv",
    exportFetch: async (q) => {
      const res = await fetchClientPurchases({
        search: q.search || undefined,
        settlementStatus: statusFrom(q.filters),
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    columns: [
      {
        key: "id",
        label: "Purchase",
        sortable: true,
        sortValue: (p) => p.purchaseNumber ?? String(p.id),
        render: (p) => (
          <Button
            variant="text"
            onClick={() => setSelectedId(p.id)}
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: 0,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: "var(--color-rodeo-black)",
            }}
          >
            {p.purchaseNumber ?? `#${p.id}`}
          </Button>
        ),
        exportValue: (p) => p.purchaseNumber ?? `#${p.id}`,
      },
      {
        key: "created",
        label: "Purchased",
        sortable: true,
        sortValue: (p) => p.createdAt,
        render: (p) => formatDate(p.createdAt),
      },
      {
        key: "organization",
        label: "Organization",
        sortValue: (p) => p.organizationName ?? "",
        render: (p) => p.organizationName ?? "—",
      },
      {
        key: "group",
        label: "Group",
        sortValue: (p) => p.groupName ?? "",
        render: (p) => p.groupName ?? "—",
      },
      {
        key: "credits",
        label: "Credits",
        sortable: true,
        sortValue: (p) => Number(p.credits),
        render: (p) => (
          <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
            {Number(p.credits).toLocaleString("en-US")}
          </Box>
        ),
      },
      {
        key: "price",
        label: "Charged",
        sortable: true,
        sortValue: (p) => Number(p.netPrice ?? p.price ?? 0),
        render: (p) => (
          <Box sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
            {money(p.netPrice ?? p.price, p.currency)}
          </Box>
        ),
        exportValue: (p) => p.netPrice ?? p.price ?? "",
      },
      {
        key: "card",
        label: "Card",
        sortValue: (p) => p.cardBrand ?? "",
        render: (p) => (p.cardBrand ? `${p.cardBrand} ···· ${p.cardLast4 ?? ""}`.trim() : "—"),
      },
      {
        key: "settlementStatus",
        label: "Status",
        sortValue: (p) => p.settlementStatus,
        render: (p) => {
          const c = PURCHASE_STATUS_CHIP[p.settlementStatus];
          return <Chip size="small" variant="outlined" label={c.label} color={c.color} />;
        },
        exportValue: (p) => PURCHASE_STATUS_CHIP[p.settlementStatus].label,
      },
      {
        key: "stripeFee",
        label: "Stripe fee",
        sortable: true,
        sortValue: (p) => Number(p.stripeFee ?? 0),
        render: (p) => (
          <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
            {p.stripeFee == null ? "—" : money(p.stripeFee, p.currency)}
          </Box>
        ),
        exportValue: (p) => p.stripeFee ?? "",
      },
      {
        key: "payoutAt",
        label: "Payout received",
        sortable: true,
        sortValue: (p) => p.payoutAt ?? "",
        render: (p) =>
          p.payoutAt ? (
            formatDate(p.payoutAt)
          ) : (
            <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>—</Typography>
          ),
        exportValue: (p) => formatDate(p.payoutAt),
      },
    ],
  };

  return (
    <Box>
      <BlotterNote>
        <strong>Credits bought directly by card — no invoice involved.</strong> The client charges a
        saved card and the credits are spendable immediately, which is what{" "}
        <strong>Clearing</strong> means here: the card cleared, but Stripe holds the cash for about
        two business days before depositing it, so it is not yet in GFG's bank.{" "}
        <strong>Paid</strong> means the payout landed and the Stripe fee is recorded. Filter on
        Clearing to see what is still in flight, and open a purchase to record a payout the webhook
        missed.
      </BlotterNote>
      <Blotter config={config} />
    </Box>
  );
}
